import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import { otpService, OtpType } from '../../../services/otpService';
import OtpInput from './OtpInput';
import Icon from '../../../components/AppIcon';

interface OtpVerificationProps {
  identifier: string;
  type: OtpType;
  onSuccess?: (user: any) => void;
  onCancel?: () => void;
  onResend?: () => void;
}

const OtpVerification: React.FC<OtpVerificationProps> = ({
  identifier,
  type,
  onSuccess,
  onCancel,
  onResend,
}) => {
  const navigate = useNavigate();
  const [otp, setOtp] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);

  // Start cooldown timer on mount
  useEffect(() => {
    setResendCooldown(60);
    const timer = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const handleVerify = async (code?: string) => {
    const otpCode = code || otp;
    
    if (otpCode.length !== 6) {
      setError('Please enter a valid 6-digit OTP code');
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const result = type === 'email'
        ? await otpService.verifyEmailOtp(identifier, otpCode)
        : await otpService.verifyPhoneOtp(identifier, otpCode);

      if (!result.success) {
        setError(result.error || 'Invalid OTP code. Please try again.');
        setOtp('');
        return;
      }

      // Clear rate limit after successful verification
      otpService.clearRateLimit(identifier, type);
      
      setSuccessMessage('✅ Verification successful!');

      // Check if user has a profile, create if not
      if (result.user) {
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('id', result.user.id)
          .maybeSingle();

        if (!profile) {
          // This is a new user - redirect to complete profile
          setTimeout(() => {
            navigate('/complete-profile', { 
              state: { 
                userId: result.user.id,
                email: result.user.email,
                phone: result.user.phone,
                isNewUser: true
              } 
            });
          }, 1000);
          return;
        }

        // Existing user - redirect to dashboard
        if (onSuccess) {
          onSuccess(result.user);
        } else {
          const dashboardRoutes: Record<string, string> = {
            brand: '/brand-dashboard',
            creator: '/creator-dashboard',
            agency: '/agency-dashboard',
            admin: '/admin-dashboard'
          };
          setTimeout(() => {
            navigate(dashboardRoutes[profile.role] || '/');
          }, 1000);
        }
      }
    } catch (err: any) {
      console.error('OTP verification error:', err);
      setError(err.message || 'Verification failed. Please try again.');
      setOtp('');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;

    setIsLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const result = await otpService.resendOtp(identifier, type);

      if (!result.success) {
        setError(result.error || 'Failed to resend OTP');
        return;
      }

      setSuccessMessage('✅ New OTP sent successfully!');
      setOtp('');
      setResendCooldown(60);

      // Start cooldown timer
      const timer = setInterval(() => {
        setResendCooldown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      if (onResend) {
        onResend();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to resend OTP');
    } finally {
      setIsLoading(false);
    }
  };

  const maskedIdentifier = type === 'email'
    ? identifier.replace(/(.{2})(.*)(@.*)/, '$1***$3')
    : identifier.replace(/(\+\d{2})(\d*)(\d{4})/, '$1****$3');

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-4">
          <Icon 
            name={type === 'email' ? 'Mail' : 'Smartphone'} 
            size={32} 
            className="text-primary" 
          />
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">
          Verify Your {type === 'email' ? 'Email' : 'Phone'}
        </h2>
        <p className="text-gray-400">
          We've sent a 6-digit verification code to
        </p>
        <p className="text-primary font-medium mt-1">{maskedIdentifier}</p>
      </div>

      <div className="space-y-6">
        {/* OTP Input */}
        <OtpInput
          value={otp}
          onChange={setOtp}
          onComplete={handleVerify}
          disabled={isLoading}
          error={error}
          autoFocus
        />

        {/* Success Message */}
        {successMessage && (
          <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
            <p className="text-green-400 text-sm text-center">{successMessage}</p>
          </div>
        )}

        {/* Error Message */}
        {error && !successMessage && (
          <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
            <p className="text-red-400 text-sm text-center">{error}</p>
          </div>
        )}

        {/* Verify Button */}
        <button
          onClick={() => handleVerify()}
          disabled={isLoading || otp.length !== 6}
          className={`
            w-full py-3 px-4 rounded-lg font-medium text-white
            transition-all duration-200
            ${isLoading || otp.length !== 6
              ? 'bg-gray-600 cursor-not-allowed'
              : 'bg-primary hover:bg-primary-dark shadow-lg shadow-primary/30 hover:shadow-primary/50'
            }
          `}
        >
          {isLoading ? (
            <span className="flex items-center justify-center">
              <Icon name="Loader2" size={20} className="animate-spin mr-2" />
              Verifying...
            </span>
          ) : (
            'Verify OTP'
          )}
        </button>

        {/* Resend OTP */}
        <div className="text-center">
          <p className="text-gray-400 text-sm mb-2">
            Didn't receive the code?
          </p>
          <button
            onClick={handleResend}
            disabled={isLoading || resendCooldown > 0}
            className={`
              text-sm font-medium transition-colors
              ${isLoading || resendCooldown > 0
                ? 'text-gray-500 cursor-not-allowed'
                : 'text-primary hover:text-primary-light'
              }
            `}
          >
            {resendCooldown > 0 
              ? `Resend OTP in ${resendCooldown}s`
              : 'Resend OTP'
            }
          </button>
        </div>

        {/* Cancel / Go Back */}
        {onCancel && (
          <button
            onClick={onCancel}
            disabled={isLoading}
            className="w-full py-2 text-gray-400 hover:text-white text-sm transition-colors"
          >
            ← Use a different {type === 'email' ? 'email' : 'phone number'}
          </button>
        )}
      </div>
    </div>
  );
};

export default OtpVerification;
